import {
  DropdownMenu as DropdownMenuPrimitive,
  Tooltip as TooltipPrimitive,
} from 'radix-ui';
import type { DropdownMenu as DropdownMenuNS } from 'radix-ui';
import type { Tooltip as TooltipNS } from 'radix-ui';
import {
  type ButtonHTMLAttributes,
  type ReactElement,
  type ReactNode,
  forwardRef,
  useState,
} from 'react';

import { BaseTooltip } from '../BaseTooltip';
import {
  DropdownItem,
  DropdownMenuContent,
  OptionsButton,
  OptionsIcon,
  StyledArrow,
  TooltipArrow,
  TooltipContent,
} from './styles';

export interface IBaseDropdownProps extends DropdownMenuNS.DropdownMenuProps {
  children: ReactNode;
  customTrigger?: ReactElement<HTMLButtonElement> | null;
  dataCy?: string;
  disabled?: boolean;
  zIndex?: number;
}

export interface IBaseTriggerDropdownProps
  extends ButtonHTMLAttributes<HTMLButtonElement> {}

export const BaseTriggerDropdown = forwardRef<
  HTMLButtonElement,
  IBaseTriggerDropdownProps
  // eslint-disable-next-line no-restricted-syntax
>(function BaseTriggerDropdown({ ...props }, ref): JSX.Element {
  return (
    <OptionsButton ref={ref} aria-label="More options" {...props}>
      <OptionsIcon />
    </OptionsButton>
  );
});

export const BaseDropdown = ({
  children,
  customTrigger = null,
  dataCy = 'dropdown-menu-button',
  disabled = false,
  zIndex,
  ...props
}: IBaseDropdownProps): JSX.Element => {
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);

  return (
    <DropdownMenuPrimitive.Root
      {...props}
      onOpenChange={(isOpen: boolean) => {
        setIsTooltipVisible(false);
        props.onOpenChange?.(isOpen);
      }}
    >
      <BaseDropdownTooltip message="Options" isVisible={isTooltipVisible}>
        <DropdownMenuPrimitive.Trigger
          disabled={disabled}
          asChild
          data-cy={dataCy}
          onMouseEnter={() => {
            setIsTooltipVisible(true);
          }}
          onMouseLeave={() => {
            setIsTooltipVisible(false);
          }}
        >
          {customTrigger ?? <BaseTriggerDropdown />}
        </DropdownMenuPrimitive.Trigger>
      </BaseDropdownTooltip>

      <DropdownMenuPrimitive.Portal>
        <DropdownMenuContent
          $zIndex={zIndex}
          data-cy="dropdown-menu-content"
          sideOffset={5}
        >
          {children}
          <StyledArrow />
        </DropdownMenuContent>
      </DropdownMenuPrimitive.Portal>
    </DropdownMenuPrimitive.Root>
  );
};

export interface IBaseDropdownItemProps
  extends DropdownMenuNS.DropdownMenuItemProps {
  type?: 'default' | 'large';
}

export const BaseDropdownItem = forwardRef<
  HTMLDivElement,
  IBaseDropdownItemProps
  // eslint-disable-next-line no-restricted-syntax
>(function BaseDropdownItem({ type = 'default', ...props }, ref): JSX.Element {
  return <DropdownItem ref={ref} {...props} $type={type} />;
});

export interface IBaseDropdownTooltipItemProps extends IBaseDropdownItemProps {
  showOnDisabled?: boolean;
  disabledTooltip?: string;
}

export const BaseDropdownTooltipItem = forwardRef<
  HTMLDivElement,
  IBaseDropdownTooltipItemProps
  // eslint-disable-next-line no-restricted-syntax
>(function BaseDropdownTooltipItem(
  {
    type = 'default',
    showOnDisabled = false,
    disabledTooltip,
    disabled = false,
    ...props
  },
  ref
): JSX.Element {
  if (showOnDisabled) {
    return (
      <BaseTooltip message={disabledTooltip} disabled={disabled !== true}>
        <DropdownItem ref={ref} disabled={disabled} {...props} $type={type} />
      </BaseTooltip>
    );
  }

  return (
    <BaseTooltip message={disabledTooltip} disabled={false}>
      <DropdownItem ref={ref} disabled={disabled} {...props} $type={type} />
    </BaseTooltip>
  );
});

export interface IBaseDropdownTooltipProps
  extends TooltipNS.TooltipProviderProps {
  children: ReactNode;
  message?: string;
  dataCy?: string;
  isVisible?: boolean;
}

export const BaseDropdownTooltip = ({
  children,
  message,
  isVisible = false,
  dataCy = 'tooltip-field',
  ...props
}: IBaseDropdownTooltipProps): JSX.Element => {
  return (
    <TooltipPrimitive.Provider {...props}>
      <TooltipPrimitive.Root open={isVisible} delayDuration={300}>
        <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
        <TooltipPrimitive.Portal>
          <TooltipContent sideOffset={5} data-cy={dataCy}>
            {message}
            <TooltipArrow />
          </TooltipContent>
        </TooltipPrimitive.Portal>
      </TooltipPrimitive.Root>
    </TooltipPrimitive.Provider>
  );
};
